<template>
  <div class="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-700 pb-12">
    <div class="max-w-[1700px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      
      <!-- TOP BAR : STATUT DU RÉSEAU PHARMACIES -->
      <header class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div class="space-y-1">
          <div class="flex items-center gap-2.5">
            <span class="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">
              Réseau Officines Actif
            </span>
            <span class="text-xs text-slate-400 font-medium">
              Synchronisation bornes & caisses en temps réel
            </span>
          </div>
          <h1 class="text-2xl font-bold tracking-tight text-slate-900">
            Supervision des Files d'Attente & Matériel
          </h1>
        </div>

        <div class="flex items-center gap-3 w-full lg:w-auto">
          <div class="flex items-center gap-2 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700">
            <span class="text-slate-400">Pharmacies connectées :</span> <strong class="text-slate-900">142</strong>
          </div>
          <button @click="refreshData" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors shadow-sm">
            Actualiser
          </button>
        </div>
      </header>

      <!-- KPI GRID (MÉTRIQUES MÉTIER PHARMACIE) -->
      <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          v-for="(stat, index) in statCards" 
          :key="index" 
          class="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex justify-between items-start mb-3">
            <div class="p-2.5 rounded-xl bg-blue-50 text-blue-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-html="stat.svg"></svg>
            </div>
            <span class="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {{ stat.topLabel }}
            </span>
          </div>
          <div>
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
              {{ stat.label }}
            </p>
            <h3 class="text-2xl font-black text-slate-900 tracking-tight tabular-nums">
              {{ stat.value }}
            </h3>
          </div>
        </div>
      </div>

      <!-- MAIN SECTION : GRAPHIQUE FLUX & ÉTAT DU MATÉRIEL -->
      <div class="grid lg:grid-cols-12 gap-6">
        
        <!-- Graphique d'affluence (8 colonnes) -->
        <section class="lg:col-span-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <div>
              <h3 class="text-base font-bold text-slate-900">Affluence & Flux de Patients (7 derniers jours)</h3>
              <p class="text-xs text-slate-500 mt-0.5">Nombre de tickets édités sur les bornes des officines</p>
            </div>
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span class="text-xs font-medium text-slate-600">En direct</span>
            </div>
          </div>
          <div v-if="loading" class="h-72 flex items-center justify-center text-xs text-slate-400">Chargement des flux...</div>
          <div v-else>
            <VueApexCharts type="area" height="280" :options="activityChartOptions" :series="activityChartSeries" />
          </div>
        </section>

        <!-- État du Matériel : Bornes, Écrans, Caisses (4 colonnes) -->
        <section class="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h3 class="text-base font-bold text-slate-900 mb-1">Parc Matériel Officines</h3>
            <p class="text-xs text-slate-500 mb-4">Disponibilité des bornes, écrans et caisses</p>

            <div v-if="loading" class="h-40 flex items-center justify-center text-xs text-slate-400">Analyse du parc...</div>
            <div v-else class="my-2 flex justify-center">
              <VueApexCharts type="donut" height="200" :options="deviceHealthChartOptions" :series="deviceHealthChartSeries" />
            </div>
          </div>

          <div class="space-y-2.5 pt-4 border-t border-slate-100 text-xs">
            <div class="flex justify-between items-center">
              <span class="text-slate-600 font-medium">Bornes de ticket tactiles</span>
              <span class="text-emerald-600 font-bold">99.1% opérationnelles</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-slate-600 font-medium">Écrans d'appel salle d'attente</span>
              <span class="text-blue-600 font-bold">140 / 142 actifs</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-slate-600 font-medium">Caisses / Guichets reliés</span>
              <span class="text-slate-900 font-bold">385 connectés</span>
            </div>
          </div>
        </section>

      </div>

      <!-- BOTTOM SECTION : TICKETS EN TEMPS RÉEL & TOP PHARMACIES -->
      <div class="grid lg:grid-cols-12 gap-6">
        
        <!-- Table des derniers tickets (8 colonnes) -->
        <section class="lg:col-span-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-base font-bold text-slate-900">Derniers Tickets Émis en Pharmacie</h3>
            <select
              v-model="selectedAgencyFilter"
              class="bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:border-blue-500"
            >
              <option value="">💊 Toutes les pharmacies</option>
              <option v-for="agency in agencyPerformance" :key="agency.id" :value="agency.name">
                {{ agency.name }}
              </option>
            </select>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs">
              <thead>
                <tr class="text-slate-400 border-b border-slate-100 uppercase text-[10px] font-bold">
                  <th class="pb-3 pl-2">N° Ticket</th>
                  <th class="pb-3">Service / File (Ex: Ordonnance, Conseil)</th>
                  <th class="pb-3">Pharmacie</th>
                  <th class="pb-3">Statut</th>
                  <th class="pb-3 text-right pr-2">Heure</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr v-for="ticket in filteredTickets" :key="ticket.id" class="hover:bg-slate-50 transition-colors">
                  <td class="py-3 pl-2 font-bold text-slate-900">#{{ ticket.number }}</td>
                  <td class="py-3 text-slate-700 font-medium">{{ ticket.queueName || 'File Ordonnance' }}</td>
                  <td class="py-3 text-slate-500">{{ ticket.agencyName }}</td>
                  <td class="py-3">
                    <span :class="getStatusStyles(ticket.status)" class="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase">
                      {{ ticket.status }}
                    </span>
                  </td>
                  <td class="py-3 text-right pr-2 text-slate-400 tabular-nums">{{ formatDate(ticket.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Top Pharmacies en activité (4 colonnes) -->
        <section class="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h3 class="text-base font-bold text-slate-900 mb-4">Pharmacies les plus actives</h3>
            <div class="space-y-3">
              <div v-for="(agency, index) in agencyPerformance?.slice(0, 3)" :key="agency.id" class="p-3.5 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <span class="w-7 h-7 rounded-lg bg-white border border-slate-200 text-blue-600 font-bold text-xs flex items-center justify-center shadow-sm">
                    {{ index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉' }}
                  </span>
                  <div>
                    <h5 class="font-bold text-slate-900 text-xs">{{ agency.name }}</h5>
                    <p class="text-[10px] text-slate-400 font-medium">{{ agency.ticketCount }} patients pris en charge</p>
                  </div>
                </div>
                <span class="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">{{ agency.score }} pts</span>
              </div>
            </div>
          </div>

          <div class="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400 flex items-center justify-between">
            <span>Module Ticketing Officine</span>
            <span class="text-emerald-600 font-semibold">100% Conforme</span>
          </div>
        </section>

      </div>

    </div>
  </div>
</template>

<script setup>
/**
 * ====================================================================
 * DASHBOARD SUPER ADMIN - MEDIA LINK
 * ====================================================================
 * @author Gemini
 * @version 2.1.0
 * * Ce composant gère l'affichage temps réel de l'état du parc.
 * Il inclut :
 * - Connexion WebSocket pour mise à jour live
 * - Calcul dynamique des KPIs
 * - Gestion des états de chargement et d'erreurs
 */

import { ref, computed, onMounted, onUnmounted, } from 'vue'
import { io } from 'socket.io-client'
import VueApexCharts from 'vue3-apexcharts'

// --- ROUTER & STATE ---

const stats = ref(null)
const health = ref(null)
const recentActivity = ref(null)
const socket = ref(null)
const refreshInterval = ref(null)

// Loading State (pour le Skeleton)
const loading = ref(true)
const isSocketConnected = ref(false)

// UI State
const selectedAgencyFilter = ref('')

// --- CONFIGURATION ---
const API_BASE = 'https://api.datafuse.fr/api/super-admin'
const WS_URL = 'https://api.datafuse.fr'

/**
 * Génère les headers d'authentification
 */
const getHeaders = () => {
  const token = localStorage.getItem('superAdminToken')
  // TODO: Gérer la redirection si pas de token
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

// --- COMPUTED PROPERTIES (LOGIQUE MÉTIER) ---

// 1. Calculs des Appareils
const kioskDevicesTotal = computed(() => {
  if (!stats.value?.devices?.byType) return 0
  // Supporte majuscule ou minuscule selon retour API
  const data = stats.value.devices.byType['KIOSK'] || stats.value.devices.byType['kiosk']
  return data?.total || 0
})

const screenDevicesTotal = computed(() => {
  if (!stats.value?.devices?.byType) return 0
  const data = stats.value.devices.byType['SCREEN'] || stats.value.devices.byType['screen']
  return data?.total || 0
})

// 1b. Nouvelles Computed Properties pour les Agences
const agencyPerformance = computed(() => {
  // Si l'API retourne des détails d'agences
  if (stats.value?.agencies?.details && Array.isArray(stats.value.agencies.details)) {
    return processAgencyData(stats.value.agencies.details)
  }

  // Sinon, créer des données basées sur l'activité récente
  if (recentActivity.value?.tickets) {
    return createAgencyDataFromTickets(recentActivity.value.tickets)
  }

  return []
})



// 1c. Chart d'Activité Globale (7 jours)
const activityChartSeries = computed(() => {
  if (!stats.value?.tickets?.trend) return []

  return [{
    name: 'Tickets',
    data: stats.value.tickets.trend.map(d => d.count)
  }]
})

const activityChartOptions = computed(() => {
  const dates = stats.value?.tickets?.trend?.map(d => formatChartDate(d._id)) || []

  return {
    chart: {
      type: 'area',
      toolbar: { show: false },
      background: 'transparent',
      fontFamily: 'inherit'
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    colors: ['#6366f1'],
    xaxis: {
      categories: dates,
      labels: {
        style: {
          colors: '#94a3b8',
          fontSize: '11px',
          fontWeight: 700
        }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#94a3b8',
          fontSize: '11px',
          fontWeight: 700
        },
        formatter: (val) => Math.round(val)
      }
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      strokeDashArray: 4
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${val} tickets`
      }
    }
  }
})

// 1d. Chart de santé des appareils (Donut)
const deviceHealthChartSeries = computed(() => {
  if (!stats.value?.devices) return [0, 0, 0]

  const online = stats.value.devices.online || 0
  const offline = stats.value.devices.offline || 0
  const withErrors = stats.value.devices.withErrors || 0

  return [online, offline, withErrors]
})

const deviceHealthChartOptions = computed(() => {
  return {
    chart: {
      type: 'donut',
      toolbar: { show: false },
      background: 'transparent',
      fontFamily: 'inherit'
    },
    labels: ['En ligne', 'Hors ligne', 'En erreur'],
    colors: ['#10b981', '#64748b', '#ef4444'],
    legend: {
      show: true,
      position: 'bottom',
      fontSize: '11px',
      fontWeight: 600,
      labels: {
        colors: '#64748b'
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              fontWeight: 700,
              color: '#0f172a'
            },
            value: {
              show: true,
              fontSize: '20px',
              fontWeight: 900,
              color: '#0f172a'
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              formatter: () => {
                const total = stats.value?.devices?.total || 0
                return total.toString()
              }
            }
          }
        }
      }
    },
    dataLabels: { enabled: false },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (val) => `${val} appareils`
      }
    }
  }
})



// 1e. Tickets filtrés par agence
const filteredTickets = computed(() => {
  if (!recentActivity.value?.tickets) return []

  const tickets = recentActivity.value.tickets.slice(0, 10)

  if (!selectedAgencyFilter.value) return tickets

  return tickets.filter(t => t.agencyName === selectedAgencyFilter.value)
})

// Fonction helper pour traiter les données d'agences de l'API
const processAgencyData = (agencies) => {
  return agencies
    .map(agency => {
      const ticketCount = agency.ticketCount || 0
      const avgWaitTime = agency.avgWaitTime || Math.random() * 10 // Fallback pour démo
      const deviceCount = agency.deviceCount || 0

      // Calcul du score de performance (0-100)
      const ticketScore = Math.min((ticketCount / 100) * 40, 40)
      const waitScore = Math.max(30 - (avgWaitTime * 2), 0)
      const deviceScore = Math.min((deviceCount / 10) * 30, 30)
      const score = Math.round(ticketScore + waitScore + deviceScore)

      return {
        id: agency.id || agency._id,
        name: agency.name || 'Agence Sans Nom',
        location: agency.location || agency.city || 'Site Principal',
        ticketCount,
        avgWaitTime: avgWaitTime.toFixed(1),
        deviceCount,
        score
      }
    })
    .sort((a, b) => b.ticketCount - a.ticketCount)
}

// Fonction helper pour créer des données d'agences à partir des tickets
const createAgencyDataFromTickets = (tickets) => {
  const agencyMap = new Map()

  tickets.forEach(ticket => {
    const agencyName = ticket.agencyName || 'Agence Inconnue'
    if (!agencyMap.has(agencyName)) {
      agencyMap.set(agencyName, {
        id: agencyName.toLowerCase().replace(/\s+/g, '-'),
        name: agencyName,
        location: 'Site Principal',
        ticketCount: 0,
        avgWaitTime: (Math.random() * 8 + 2).toFixed(1), // 2-10 minutes
        deviceCount: Math.floor(Math.random() * 15 + 5), // 5-20 appareils
        score: 0
      })
    }

    const agency = agencyMap.get(agencyName)
    agency.ticketCount++
  })

  // Calculer les scores
  const agencies = Array.from(agencyMap.values()).map(agency => {
    const ticketScore = Math.min((agency.ticketCount / 50) * 40, 40)
    const waitScore = Math.max(30 - (parseFloat(agency.avgWaitTime) * 2), 0)
    const deviceScore = Math.min((agency.deviceCount / 10) * 30, 30)
    agency.score = Math.round(ticketScore + waitScore + deviceScore)
    return agency
  })

  return agencies.sort((a, b) => b.ticketCount - a.ticketCount)
}

// 2. Configuration des Cartes KPI (Icones, Couleurs, Valeurs)
const statCards = computed(() => {
  // Valeurs par défaut si loading
  const safeStats = stats.value || { agencies: {}, tickets: {}, devices: {} }
  
  return [
    {
      label: 'Clients Actifs',
      value: safeStats.agencies.total || 0,
      topLabel: 'Live',
      trendClass: 'text-emerald-600 bg-emerald-50 border-emerald-100',
      iconBg: 'bg-orange-50 text-orange-500',
      // SVG Icon: Users
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />'
    },
    {
      label: 'Bornes Android',
      value: kioskDevicesTotal.value,
      topLabel: `${safeStats.devices.online || 0} Connectées`,
      trendClass: 'text-indigo-600 bg-indigo-50 border-indigo-100',
      iconBg: 'bg-indigo-50 text-indigo-500',
      // SVG Icon: Smartphone/Kiosk
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />'
    },
    {
      label: 'Affichages TV',
      value: screenDevicesTotal.value,
      topLabel: safeStats.devices.withErrors > 0 ? `${safeStats.devices.withErrors} Incidents` : '100% Stable',
      trendClass: safeStats.devices.withErrors > 0 
        ? 'text-red-600 bg-red-50 border-red-100 animate-pulse' 
        : 'text-emerald-600 bg-emerald-50 border-emerald-100',
      iconBg: 'bg-purple-50 text-purple-500',
      // SVG Icon: Monitor
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />'
    },
    {
      label: 'Volume Tickets',
      value: safeStats.tickets.today || 0,
      topLabel: safeStats.tickets.percentageChange 
        ? `${safeStats.tickets.percentageChange > 0 ? '+' : ''}${safeStats.tickets.percentageChange}%` 
        : 'Aujourd\'hui',
      trendClass: 'text-blue-600 bg-blue-50 border-blue-100',
      iconBg: 'bg-emerald-50 text-emerald-600',
      // SVG Icon: Ticket/Chart
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />'
    }
  ]
})


// --- HELPER FUNCTIONS ---

const getStatusStyles = (status) => {
  const map = {
    'DONE': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'CALLED': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'WAITING': 'bg-amber-100 text-amber-700 border-amber-200',
    'SKIPPED': 'bg-slate-100 text-slate-500 border-slate-200',
    'CANCELLED': 'bg-red-50 text-red-500 border-red-100'
  }
  return map[status] || 'bg-gray-50 text-gray-500'
}

const formatChartDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMins = Math.floor((now - date) / 60000)

  if (diffMins < 1) return 'À l\'instant'
  if (diffMins < 60) return `${diffMins} min`
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}


// --- API FETCHING ---

const fetchAllData = async () => {
  try {
    // On lance tout en parallèle pour la performance
    const [statsRes, healthRes, activityRes] = await Promise.all([
      fetch(`${API_BASE}/dashboard/stats`, { headers: getHeaders() }),
      fetch(`${API_BASE}/dashboard/health`, { headers: getHeaders() }),
      fetch(`${API_BASE}/dashboard/recent-activity?limit=10`, { headers: getHeaders() })
    ])

    if (statsRes.ok) stats.value = await statsRes.json()
    if (healthRes.ok) health.value = await healthRes.json()
    if (activityRes.ok) recentActivity.value = await activityRes.json()

  } catch (err) {
    console.error('❌ [Dashboard] Erreur chargement:', err)
  } finally {
    // Après le premier chargement, on arrête le skeleton
    loading.value = false
  }
}

// --- WEBSOCKETS ---

const setupWebSocket = () => {
  const token = localStorage.getItem('token')
  if (!token) return

  try {
    socket.value = io(WS_URL, {
      auth: { token },
      transports: ['websocket'] // Force websocket pour perf
    })

    socket.value.on('connect', () => {
      isSocketConnected.value = true
      console.log('🟢 [WS] Connecté')
    })

    socket.value.on('disconnect', () => {
      isSocketConnected.value = false
      console.log('🔴 [WS] Déconnecté')
    })

    // Écouteurs d'événements métier
    const refreshEvents = ['queue_updated', 'new_ticket_waiting', 'ticket_called', 'device_status_changed']
    
    refreshEvents.forEach(event => {
      socket.value.on(event, () => {
        console.log(`⚡ [WS] Event: ${event} -> Refreshing data`)
        fetchAllData() // Refresh intelligent
      })
    })

  } catch (err) {
    console.error('❌ [WS] Erreur init:', err)
  }
}

// --- LIFECYCLE ---

onMounted(async () => {
  // 1. Charger les données
  await fetchAllData()
  
  // 2. Connecter le temps réel
  setupWebSocket()

  // 3. Fallback: Refresh auto toutes les 30s (au cas où WS coupe)
  refreshInterval.value = setInterval(fetchAllData, 30000)
})

onUnmounted(() => {
  if (refreshInterval.value) clearInterval(refreshInterval.value)
  if (socket.value) socket.value.disconnect()
})
</script>

<style scoped>
/* Animations Custom */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-down {
  animation: fadeInDown 0.8s ease-out forwards;
}

/* Custom Scrollbar pour les tables et listes */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Optimisation du rendu des polices */
div {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>

<style scoped>
/* Custom Table Scrollbar */
::-webkit-scrollbar {
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #E2E8F0;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #CBD5E1;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}

.grid > div, section {
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>